import {
  Position,
  ViewportPortal,
  type ConnectionLineComponentProps,
  type EdgeProps,
} from '@xyflow/react';
import { useEffect, useLayoutEffect, useRef } from 'react';

// Spring tuning. Lower stiffness + lower damping = bigger, longer wobble.
// `INERTIA` translates raw endpoint motion (pixels) into a velocity tick
// applied to the spring; turn it up for a wilder bungelen effect.
const STIFFNESS = 80;
const DAMPING = 5.4;
const INERTIA = 6;
const INTRO_VELOCITY = 180; // px/s, kick applied to a freshly drawn edge

// Radius used to route self-loops (source === target) around the side of
// the node, so the curve never crosses behind the module card.
const SELF_LOOP_RADIUS = 140;

// Edge IDs that should play the intro bounce on next mount. App.tsx adds
// to this Set from `onConnect` right before calling setEdges, so we don't
// have to thread a `fresh` flag through the edge's `data` field — that
// extra prop changed how React Flow memoises the edge wrapper and made
// position updates silently skip our drag-wobble effect.
export const freshEdgeIds = new Set<string>();

// Tracks which edge IDs have already played their intro bounce. Lives at
// module scope so React StrictMode's double-mount cycle doesn't fire the
// animation twice, and so subsequent React Flow re-renders (e.g. node
// drag, state syncs) never replay it on the same edge.
const introPlayedIds = new Set<string>();

// Map of edge id → imperative bounce trigger. Each mounted ElasticEdge
// registers a callback here so the parent app can kick its spring
// without going through the React state path — used for the spring-back
// effect on rejected edge-reconnects.
export interface BounceOpts {
  /**
   * Which end was grabbed during the rejected reconnect drag. The
   * spring on that end is the one we displace toward the cursor's last
   * position so the rebound is visibly anchored to the right side.
   */
  handleType?: 'source' | 'target';
  /** Cursor offset (flow coords) from the grabbed endpoint at release. */
  offsetX?: number;
  offsetY?: number;
}
const bounceTriggers = new Map<string, (opts?: BounceOpts) => void>();

// Bounces queued for edges that aren't currently mounted. Critical for
// the rejected-reconnect case: React Flow unmounts the EdgeComponent
// while the user is mid-drag (see `!reconnecting && jsx(EdgeComponent)`
// in @xyflow/react), so on `onReconnectEnd` no bounce trigger is
// registered yet. We stash the desired bounce here and the next mount
// consumes it from its registration effect.
const pendingBounces = new Map<string, BounceOpts>();

/**
 * Kick the spring of a specific edge as if it had just been freshly
 * drawn. When `opts.handleType` + `opts.offsetX/Y` are supplied, the
 * spring on that end is pre-displaced to the cursor's last position
 * (with zero velocity) so the curve appears stretched toward the
 * cursor at the moment of release and then springs back to rest — the
 * "bungelend terug"-effect for a rejected reconnect.
 *
 * If the edge isn't currently mounted (which happens during a reconnect
 * drag — React Flow temporarily swaps the edge out for its anchor-drag
 * surface) we queue the bounce; the next mount will play it.
 */
export function triggerEdgeBounce(id: string, opts?: BounceOpts) {
  const handler = bounceTriggers.get(id);
  if (handler) {
    handler(opts);
  } else {
    pendingBounces.set(id, opts ?? {});
  }
}

// Spring state for one bezier control point.
interface Spring {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

// "Elastic" custom edge.
//
// Each edge maintains two spring-damped wobble offsets — one per bezier
// control point — that are applied on top of the regular curve. When an
// endpoint moves the spring closest to that endpoint receives a velocity
// kick in the opposite direction, so the curve appears to trail behind the
// moving node and then swing back to rest. Newly created edges (flagged
// `data.fresh` by `onConnect`) get a one-shot intro bounce.
//
// The animation loop mutates the SVG path's `d` attribute directly via a
// ref so we never trigger React re-renders while a spring is in flight.
// This keeps the canvas smooth even when many edges are wobbling at once
// (e.g. dragging a node with five connected exits).
export function ElasticEdge(props: EdgeProps) {
  const {
    id,
    source,
    target,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    markerEnd,
    markerStart,
    style,
    selected,
    interactionWidth,
  } = props;
  // True self-loop: the edge starts and ends on the same node. Routed via
  // a sideways C-curve so it can't disappear behind the node card.
  const isSelfLoop = source === target;

  const pathRef = useRef<SVGPathElement | null>(null);
  const physRef = useRef<{ s: Spring; t: Spring }>({
    s: { x: 0, y: 0, vx: 0, vy: 0 },
    t: { x: 0, y: 0, vx: 0, vy: 0 },
  });
  const rafRef = useRef<number | null>(null);
  // Keep the latest props in a ref so the animation loop reads fresh values
  // without needing to be re-created on every render.
  const propsRef = useRef({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, isSelfLoop });
  propsRef.current = { sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, isSelfLoop };

  function buildPath() {
    const p = propsRef.current;
    const ph = physRef.current;
    const [cp1x, cp1y, cp2x, cp2y] = p.isSelfLoop
      ? selfLoopControlPoints(p.sourceX, p.sourceY, p.targetX, p.targetY)
      : controlPoints(
          p.sourceX,
          p.sourceY,
          p.sourcePosition,
          p.targetX,
          p.targetY,
          p.targetPosition,
        );
    return (
      `M ${p.sourceX} ${p.sourceY}` +
      ` C ${cp1x + ph.s.x} ${cp1y + ph.s.y}` +
      ` ${cp2x + ph.t.x} ${cp2y + ph.t.y}` +
      ` ${p.targetX} ${p.targetY}`
    );
  }

  function ensureRaf() {
    if (rafRef.current != null) return;
    let last = performance.now();
    const step = (now: number) => {
      const dt = Math.min(0.032, (now - last) / 1000);
      last = now;
      const ph = physRef.current;
      stepSpring(ph.s, dt);
      stepSpring(ph.t, dt);
      if (isAtRest(ph.s) && isAtRest(ph.t)) {
        ph.s = { x: 0, y: 0, vx: 0, vy: 0 };
        ph.t = { x: 0, y: 0, vx: 0, vy: 0 };
        if (pathRef.current) pathRef.current.setAttribute('d', buildPath());
        rafRef.current = null;
        return;
      }
      if (pathRef.current) pathRef.current.setAttribute('d', buildPath());
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }

  // Detect endpoint motion during render. We tried both useEffect with
  // [sourceX, sourceY, …] deps and an imperative storeApi.subscribe —
  // both worked for already-mounted edges but mysteriously refused to
  // fire for ones added during the same session. Doing the comparison
  // inline in the render body sidesteps that entirely: React renders the
  // edge whenever React Flow recomputes its endpoint positions, so this
  // code path is guaranteed to see the latest sourceX/sourceY props.
  //
  // Side-effecting in render is normally a no-no, but here we only
  // touch refs (idempotent) and call requestAnimationFrame through a
  // guard, so duplicate renders (e.g. React StrictMode) can't double-
  // kick the spring — a second render with the same props produces a
  // zero delta and skips the kick.
  const lastEndpointsRef = useRef<{ sx: number; sy: number; tx: number; ty: number } | null>(null);
  const prevEnd = lastEndpointsRef.current;
  if (prevEnd == null) {
    lastEndpointsRef.current = { sx: sourceX, sy: sourceY, tx: targetX, ty: targetY };
  } else if (
    prevEnd.sx !== sourceX ||
    prevEnd.sy !== sourceY ||
    prevEnd.tx !== targetX ||
    prevEnd.ty !== targetY
  ) {
    const dsx = sourceX - prevEnd.sx;
    const dsy = sourceY - prevEnd.sy;
    const dtx = targetX - prevEnd.tx;
    const dty = targetY - prevEnd.ty;
    lastEndpointsRef.current = { sx: sourceX, sy: sourceY, tx: targetX, ty: targetY };
    let kicked = false;
    if (Math.abs(dsx) > 0.01 || Math.abs(dsy) > 0.01) {
      physRef.current.s.vx += -dsx * INERTIA;
      physRef.current.s.vy += -dsy * INERTIA;
      kicked = true;
    }
    if (Math.abs(dtx) > 0.01 || Math.abs(dty) > 0.01) {
      physRef.current.t.vx += -dtx * INERTIA;
      physRef.current.t.vy += -dty * INERTIA;
      kicked = true;
    }
    if (kicked) ensureRaf();
  }

  // Intro bounce for freshly-drawn edges. The edge ID is registered in
  // `freshEdgeIds` by `onConnect` immediately before adding the edge to
  // state, so on first mount we know whether to play the snap-in
  // animation. We DO NOT put the flag on `edge.data` because that turned
  // out to make React Flow take a different memoised path through the
  // edge wrapper that swallowed subsequent prop updates.
  useEffect(() => {
    if (introPlayedIds.has(id)) return;
    introPlayedIds.add(id);
    if (!freshEdgeIds.delete(id)) return;
    physRef.current.s.vy = -INTRO_VELOCITY;
    physRef.current.t.vy = -INTRO_VELOCITY;
    ensureRaf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Register a global bounce trigger so the parent app can imperatively
  // play the bounce animation (e.g. when a reconnect drag is dropped on
  // empty space and the edge snaps back to its original endpoints).
  //
  // useLayoutEffect rather than useEffect: when this is a remount after
  // a rejected reconnect there's a queued bounce to consume, and we
  // want to apply the spring displacement + first path update BEFORE
  // paint so the user never sees the edge flash at its rest position.
  useLayoutEffect(() => {
    const fire = (opts?: BounceOpts) => {
      if (
        opts?.handleType &&
        (opts.offsetX !== undefined || opts.offsetY !== undefined)
      ) {
        // Snap-back from a rejected reconnect drag: pre-displace the
        // spring on the grabbed end so the curve renders stretched
        // toward where the cursor was, then let the spring naturally
        // pull it back through the rest position (underdamped → it
        // overshoots a bit, giving the bungelen-terug visual).
        const spring = opts.handleType === 'source' ? physRef.current.s : physRef.current.t;
        // Damp the offset a little so very long drags don't produce
        // wild oscillations that fly off-screen.
        const SCALE = 0.55;
        spring.x = (opts.offsetX ?? 0) * SCALE;
        spring.y = (opts.offsetY ?? 0) * SCALE;
        spring.vx = 0;
        spring.vy = 0;
      } else {
        // Default intro-style bounce.
        physRef.current.s.vy = -INTRO_VELOCITY;
        physRef.current.t.vy = -INTRO_VELOCITY;
      }
      // Push the new spring state into the DOM immediately so the bounce
      // is visible from the very next paint (important when this fire is
      // being run from a queued bounce on remount).
      if (pathRef.current) pathRef.current.setAttribute('d', buildPath());
      ensureRaf();
    };
    bounceTriggers.set(id, fire);
    // Consume any bounce that was queued while this edge was unmounted
    // (typical case: React Flow swapped us out for a reconnect-anchor
    // drag and `onReconnectEnd` called triggerEdgeBounce before our
    // remount completed).
    const queued = pendingBounces.get(id);
    if (queued !== undefined) {
      pendingBounces.delete(id);
      fire(queued);
    } else if (!isAtRest(physRef.current.s) || !isAtRest(physRef.current.t)) {
      // React StrictMode runs cleanup→setup an extra time on mount, so
      // a RAF kicked off by `fire()` in the previous setup will already
      // have been cancelled by the RAF cleanup effect by the time we
      // re-mount here. The spring state itself survives (refs persist
      // across StrictMode's simulated cycle), so if it isn't at rest we
      // need to relaunch the animation, otherwise the edge freezes at
      // the displaced position — the "rare knik" the user spotted.
      if (pathRef.current) pathRef.current.setAttribute('d', buildPath());
      ensureRaf();
    }
    return () => {
      if (bounceTriggers.get(id) === fire) bounceTriggers.delete(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Always cancel any in-flight RAF on unmount. We MUST null out the
  // ref after cancelling — otherwise ensureRaf on the next mount sees a
  // stale (already-cancelled) handle, thinks the loop is still alive
  // and bails out. That hits us hard in React StrictMode, whose extra
  // cleanup→setup cycle would otherwise leave the spring frozen at its
  // displaced position with no animation running ("rare knik").
  useEffect(
    () => () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    },
    [],
  );

  // Initial path uses the current spring state (zeros on first mount, or
  // the live values on subsequent re-renders so we don't flicker).
  const dStr = buildPath();
  const hitWidth = interactionWidth ?? 20;
  return (
    <>
      {/* Wide invisible hit-target so the user can click the edge to select it. */}
      <path
        className="react-flow__edge-interaction"
        d={dStr}
        fill="none"
        stroke="transparent"
        strokeWidth={hitWidth}
      />
      <path
        ref={pathRef}
        id={id}
        className={`react-flow__edge-path${selected ? ' selected' : ''}`}
        d={dStr}
        fill="none"
        style={style}
        markerEnd={markerEnd}
        markerStart={markerStart}
      />
      {/* Endpoint anchor dots — visible only when the edge is selected.
          Rendered through `ViewportPortal` so they end up in the
          `.react-flow__viewport-portal` div, which is the LAST child of
          the React Flow viewport and therefore stacks ON TOP of the
          nodes / handles. (EdgeLabelRenderer's portal target sits
          BEFORE NodeRenderer in the DOM, so dots placed there get
          covered by the grey input/exit handle dots.)
          `pointer-events: none` on the dot keeps the handle's own
          hit-test — and React Flow's reconnect-anchor grab — intact. */}
      {selected && (
        <ViewportPortal>
          <div
            className="fb-elastic-anchor"
            style={{
              transform: `translate(-50%, -50%) translate(${sourceX}px, ${sourceY}px)`,
            }}
          />
          <div
            className="fb-elastic-anchor"
            style={{
              transform: `translate(-50%, -50%) translate(${targetX}px, ${targetY}px)`,
            }}
          />
        </ViewportPortal>
      )}
    </>
  );
}

function stepSpring(s: Spring, dt: number) {
  // 2nd-order spring: a = -k*x - c*v.
  const ax = -STIFFNESS * s.x - DAMPING * s.vx;
  const ay = -STIFFNESS * s.y - DAMPING * s.vy;
  s.vx += ax * dt;
  s.vy += ay * dt;
  s.x += s.vx * dt;
  s.y += s.vy * dt;
}

function isAtRest(s: Spring) {
  return (
    Math.abs(s.x) < 0.2 && Math.abs(s.y) < 0.2 && Math.abs(s.vx) < 0.6 && Math.abs(s.vy) < 0.6
  );
}

// Bezier control points. We compute them ourselves (rather than calling
// React Flow's `getBezierPath`) so we have explicit access to add the
// spring wobble. The formula is a simplification of React Flow's default
// curvature — the control points extend half the straight-line distance
// along each handle's outward normal, which gives a smooth S/C curve for
// the common Bottom→Top edges in our flow designer.
function controlPoints(
  sourceX: number,
  sourceY: number,
  sourcePos: Position | undefined,
  targetX: number,
  targetY: number,
  targetPos: Position | undefined,
): [number, number, number, number] {
  const dist = Math.hypot(targetX - sourceX, targetY - sourceY) * 0.5;
  const sd = directionVector(sourcePos);
  const td = directionVector(targetPos);
  return [
    sourceX + sd[0] * dist,
    sourceY + sd[1] * dist,
    targetX + td[0] * dist,
    targetY + td[1] * dist,
  ];
}

// Self-loop routing: the source is on the bottom of the node, the target
// on the top of the same node. We send both control points outward by
// `SELF_LOOP_RADIUS` on the side that the source handle leans towards
// (right by default, left if the source handle sits left of the target
// handle), so the resulting curve makes a big "C" or backwards-"C" that
// clears the module card on one side instead of cutting across it.
function selfLoopControlPoints(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
): [number, number, number, number] {
  // Sign: +1 → loop right of the node, -1 → left. When the source/target
  // sit at the same X (centered exit), default to right.
  const side = sourceX < targetX ? -1 : 1;
  const R = SELF_LOOP_RADIUS;
  // cp1: outward from source, dropped down a bit so the curve bulges
  // below the node before climbing back up.
  // cp2: outward from target, lifted up so the curve enters from above.
  // Together these two control points define a symmetric "D" shape
  // wrapping around the node on the chosen side.
  return [
    sourceX + side * R,
    sourceY + R * 0.6,
    targetX + side * R,
    targetY - R * 0.6,
  ];
}

function directionVector(p: Position | undefined): [number, number] {
  switch (p) {
    case Position.Top:
      return [0, -1];
    case Position.Bottom:
      return [0, 1];
    case Position.Left:
      return [-1, 0];
    case Position.Right:
      return [1, 0];
    default:
      return [0, 1];
  }
}

// "Elastic" connection line — what the user sees WHILE dragging a new
// connection from a handle. The "from" end is anchored to the source
// handle; the "to" end follows the mouse cursor. We apply spring physics
// to the target-side control point so the curve drags behind the cursor
// and snaps back when the mouse pauses, matching the placed-edge feel.
export function ElasticConnectionLine(props: ConnectionLineComponentProps) {
  const { fromX, fromY, fromPosition, toX, toY, toPosition, connectionLineStyle } = props;

  const pathRef = useRef<SVGPathElement | null>(null);
  const physRef = useRef<{ s: Spring; t: Spring }>({
    s: { x: 0, y: 0, vx: 0, vy: 0 },
    t: { x: 0, y: 0, vx: 0, vy: 0 },
  });
  const prevRef = useRef<{ tx: number; ty: number } | null>(null);
  const rafRef = useRef<number | null>(null);
  const propsRef = useRef({ fromX, fromY, fromPosition, toX, toY, toPosition });
  propsRef.current = { fromX, fromY, fromPosition, toX, toY, toPosition };

  function buildPath() {
    const p = propsRef.current;
    const ph = physRef.current;
    // While dragging into open space `toPosition` is whatever React Flow
    // guessed (usually the opposite of `fromPosition`), which is fine.
    const [cp1x, cp1y, cp2x, cp2y] = controlPoints(
      p.fromX,
      p.fromY,
      p.fromPosition,
      p.toX,
      p.toY,
      p.toPosition,
    );
    return (
      `M ${p.fromX} ${p.fromY}` +
      ` C ${cp1x + ph.s.x} ${cp1y + ph.s.y}` +
      ` ${cp2x + ph.t.x} ${cp2y + ph.t.y}` +
      ` ${p.toX} ${p.toY}`
    );
  }

  function ensureRaf() {
    if (rafRef.current != null) return;
    let last = performance.now();
    const step = (now: number) => {
      const dt = Math.min(0.032, (now - last) / 1000);
      last = now;
      const ph = physRef.current;
      stepSpring(ph.s, dt);
      stepSpring(ph.t, dt);
      if (isAtRest(ph.s) && isAtRest(ph.t)) {
        ph.s = { x: 0, y: 0, vx: 0, vy: 0 };
        ph.t = { x: 0, y: 0, vx: 0, vy: 0 };
        if (pathRef.current) pathRef.current.setAttribute('d', buildPath());
        rafRef.current = null;
        return;
      }
      if (pathRef.current) pathRef.current.setAttribute('d', buildPath());
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }

  // The "from" handle is anchored, so only the cursor end gets a kick.
  useEffect(() => {
    const prev = prevRef.current;
    if (prev == null) {
      prevRef.current = { tx: toX, ty: toY };
      return;
    }
    const dtx = toX - prev.tx;
    const dty = toY - prev.ty;
    prevRef.current = { tx: toX, ty: toY };
    if (Math.abs(dtx) < 0.01 && Math.abs(dty) < 0.01) return;
    physRef.current.t.vx += -dtx * INERTIA;
    physRef.current.t.vy += -dty * INERTIA;
    ensureRaf();
  }, [toX, toY]);

  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const dStr = buildPath();
  return (
    <g>
      <path
        ref={pathRef}
        d={dStr}
        fill="none"
        className="react-flow__connection-path"
        style={connectionLineStyle}
      />
    </g>
  );
}
